import { z } from 'zod';
import { DomainError } from '../errors.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';
import { MAX_REQUISITION_LINES } from './requisition.js';

/**
 * Ofisin talebi düzenlemesi (ADR 0111 §A; sahip 2026-10-23: "ofis geminin verdiği talebi modifiye
 * edip gönderebilir: örnek 100 kilo eti 150 yapabilir ya da nutella varsa ne nutellası deyip onu
 * tamamen listeden kaldırabilir. gemi buna uyar, ona onaylanan liste gelir ve gemi bu listeye göre
 * hareket eder").
 *
 * Gemi talebini değiştirmez (mimari belge §8): ofis değiştirilemez bir revizyon yazar
 * (`requisition_office_revisions`), revizyon yalnız o gemiye iner. Her revizyon bütün talep
 * satırlarını kapsar; geçerli olan sonuncusudur. Onaylı liste talep satırlarından ve son
 * revizyondan türer (`approvedList`). Hata iletileri koddur; arayüz çevirir.
 */

/** Ofisin bir revizyonda ekleyebileceği en çok katalog kalemi. */
export const MAX_ADDED_LINES = 50;
const MAX_QTY = 1_000_000_000;

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const id = () => z.uuid(typed('INVALID_ID'));
const quantity = () =>
  z.number(typed('INVALID_NUMBER')).gt(0, 'OUT_OF_RANGE').max(MAX_QTY, 'OUT_OF_RANGE');
const note = () =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(300, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

/** Talep satırının ofis kararı: onaylanan miktar ya da kaldırma. */
export const revisionLineSchema = z.discriminatedUnion('removed', [
  z.strictObject({
    requisitionLineId: id(),
    removed: z.literal(false),
    quantity: quantity(),
    note: note(),
  }),
  z.strictObject({
    requisitionLineId: id(),
    removed: z.literal(true),
    note: note(),
  }),
]);
export type RevisionLineInput = z.output<typeof revisionLineSchema>;

/** Ofisin eklediği satır: yalnız katalog kalemi (sorular #1172); ad ve birim katalogdan. */
export const revisionAddedSchema = z.strictObject({
  materialId: id(),
  quantity: quantity(),
  note: note(),
});

export const requisitionRevisionSchema = z.strictObject({
  lines: z.array(revisionLineSchema).max(MAX_REQUISITION_LINES, 'TOO_MANY_LINES'),
  added: z
    .array(revisionAddedSchema)
    .max(MAX_ADDED_LINES, 'TOO_MANY_LINES')
    .optional()
    .transform((value) => value ?? []),
  /** Genel gerekçe zorunlu (sorular #1173): gemi neden değiştiğini görür. */
  reason: z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(1000, 'TOO_LONG')),
});
export type RequisitionRevisionInput = z.output<typeof requisitionRevisionSchema>;

export function parseRequisitionRevision(input: unknown): ParseResult<RequisitionRevisionInput> {
  const result = requisitionRevisionSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Kayıttaki revizyon satırı: satır numarası ve onaylanan miktar (kaldırılanda `null`). */
export interface RevisionLine {
  requisitionLineId: string;
  lineNo: number;
  removed: boolean;
  quantity: number | null;
  note: string | null;
}

/** Kayıttaki eklenen satır: kimlik (UUIDv7) yazılırken verilir; ad ve birim katalogdan. */
export interface RevisionAddedLine {
  id: string;
  materialId: string;
  name: string;
  unit: string;
  quantity: number;
  note: string | null;
}

export interface RequisitionRevisionRecord {
  revisionNo: number;
  lines: readonly RevisionLine[];
  added: readonly RevisionAddedLine[];
  reason: string;
}

/** Revizyonun karşılaştırıldığı talep satırı. */
export interface RevisableLine {
  id: string;
  lineNo: number;
  materialId: string | null;
  name: string;
  description: string | null;
  unit: string;
  quantity: number;
}

const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

/** Onaylanan miktarların karşılaştırma anahtarı: satır → miktar (kaldırılan 0), eklenen → miktar. */
function signature(
  lines: readonly { requisitionLineId: string; removed: boolean; quantity: number | null }[],
  added: readonly { materialId: string; quantity: number }[],
  notes: readonly (string | null)[],
): string {
  return JSON.stringify({
    lines: [...lines]
      .map((line) => [line.requisitionLineId, line.removed ? 0 : round(line.quantity ?? 0)])
      .sort(),
    added: [...added].map((line) => [line.materialId, round(line.quantity)]).sort(),
    notes: [...notes].sort(),
  });
}

/**
 * Revizyon talebe uyar mı (ADR 0111 §A): her talep satırı tam bir kez (`REVISION_LINES_MISMATCH`);
 * eklenen kalem talepte ya da eklenenlerde yoksa (`MATERIAL_ALREADY_LISTED`; miktar değişikliği
 * satırın kendisinde yapılır); en az bir satır kalır (`NO_APPROVED_LINES`); geçerli listeyle
 * aynı değil (`REVISION_UNCHANGED`).
 */
export function assertRevisionApplies(
  lines: readonly RevisableLine[],
  previous: RequisitionRevisionRecord | null,
  revision: RequisitionRevisionInput,
): void {
  const known = new Set(lines.map((line) => line.id));
  const seen = new Set<string>();
  for (const line of revision.lines) {
    if (!known.has(line.requisitionLineId) || seen.has(line.requisitionLineId)) {
      throw new DomainError('REVISION_LINES_MISMATCH', line.requisitionLineId);
    }
    seen.add(line.requisitionLineId);
  }
  if (seen.size !== known.size) throw new DomainError('REVISION_LINES_MISMATCH');
  const materials = new Set(lines.flatMap((line) => (line.materialId ? [line.materialId] : [])));
  for (const line of revision.added) {
    if (materials.has(line.materialId)) {
      throw new DomainError('MATERIAL_ALREADY_LISTED', line.materialId);
    }
    materials.add(line.materialId);
  }
  if (revision.added.length === 0 && revision.lines.every((line) => line.removed)) {
    throw new DomainError('NO_APPROVED_LINES');
  }
  const next = signature(
    revision.lines.map((line) => ({
      requisitionLineId: line.requisitionLineId,
      removed: line.removed,
      quantity: line.removed ? null : line.quantity,
    })),
    revision.added,
    [...revision.lines.map((line) => line.note), ...revision.added.map((line) => line.note)],
  );
  const current = previous
    ? signature(previous.lines, previous.added, [
        ...previous.lines.map((line) => line.note),
        ...previous.added.map((line) => line.note),
      ])
    : signature(
        lines.map((line) => ({
          requisitionLineId: line.id,
          removed: false,
          quantity: line.quantity,
        })),
        [],
        lines.map(() => null),
      );
  if (next === current) throw new DomainError('REVISION_UNCHANGED');
}

export type ApprovedChange = 'unchanged' | 'increased' | 'decreased' | 'removed' | 'added';

/** Onaylı listenin satırı: gemi bu listeye göre hareket eder. */
export interface ApprovedListLine {
  /** Talep satırının kimliği ya da eklenen satırın kimliği. */
  key: string;
  requisitionLineId: string | null;
  lineNo: number | null;
  materialId: string | null;
  name: string;
  description: string | null;
  unit: string;
  /** Geminin istediği; ofisin eklediği satırda `null`. */
  requested: number | null;
  /** Onaylanan; kaldırılan satırda 0. */
  approved: number;
  change: ApprovedChange;
  note: string | null;
}

/**
 * Onaylı liste (ADR 0111 §A): revizyon yoksa talep olduğu gibi; varsa son revizyonun miktarları,
 * kaldırılan satırlar (0) ve ofisin eklediği satırlar (sonda). Kaldırılan satır listede kalır:
 * gemi neyin çıkarıldığını görür.
 */
export function approvedList(
  lines: readonly RevisableLine[],
  revision: RequisitionRevisionRecord | null,
): ApprovedListLine[] {
  const decisions = new Map((revision?.lines ?? []).map((line) => [line.requisitionLineId, line]));
  const rows: ApprovedListLine[] = [...lines]
    .sort((a, b) => a.lineNo - b.lineNo)
    .map((line) => {
      const decision = decisions.get(line.id);
      const approved = decision
        ? decision.removed
          ? 0
          : round(decision.quantity ?? 0)
        : line.quantity;
      const change: ApprovedChange = decision?.removed
        ? 'removed'
        : approved > line.quantity
          ? 'increased'
          : approved < line.quantity
            ? 'decreased'
            : 'unchanged';
      return {
        key: line.id,
        requisitionLineId: line.id,
        lineNo: line.lineNo,
        materialId: line.materialId,
        name: line.name,
        description: line.description,
        unit: line.unit,
        requested: line.quantity,
        approved,
        change,
        note: decision?.note ?? null,
      };
    });
  for (const line of revision?.added ?? []) {
    rows.push({
      key: line.id,
      requisitionLineId: null,
      lineNo: null,
      materialId: line.materialId,
      name: line.name,
      description: null,
      unit: line.unit,
      requested: null,
      approved: round(line.quantity),
      change: 'added',
      note: line.note,
    });
  }
  return rows;
}

/**
 * Revizyonun içerik hash'inin girdisi (mimari belge §7.3): kapalı alan listesi, satırlar satır
 * numarasıyla sıralı. Revizyon onaylanan talep içeriğinin hash'ine bağlıdır (`requisitionHash`).
 */
export function requisitionRevisionContent(input: {
  vesselId: string;
  requisitionId: string;
  revisionNo: number;
  requisitionHash: string;
  lines: readonly RevisionLine[];
  added: readonly RevisionAddedLine[];
  reason: string;
}) {
  return {
    vesselId: input.vesselId,
    requisitionId: input.requisitionId,
    revisionNo: input.revisionNo,
    requisitionHash: input.requisitionHash,
    lines: [...input.lines]
      .sort((a, b) => a.lineNo - b.lineNo)
      .map((line) => ({
        requisitionLineId: line.requisitionLineId,
        lineNo: line.lineNo,
        removed: line.removed,
        quantity: line.quantity,
        note: line.note,
      })),
    added: input.added.map((line) => ({
      id: line.id,
      materialId: line.materialId,
      name: line.name,
      unit: line.unit,
      quantity: line.quantity,
      note: line.note,
    })),
    reason: input.reason,
  };
}
